import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Phase 3 (RBAC): the browser is NEVER authoritative — every gated action is
// re-checked on the backend via `require_permission`. This context only
// controls what the UI renders / offers so employees stop seeing controls
// they cannot use. Deny-by-default until the effective permissions arrive.
const PermissionsContext = createContext({
  permissions: null,
  can: () => false,
  loaded: false,
  refresh: () => {},
});

const EMPLOYEE_ROLES = new Set(["admin", "account_manager"]);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  // null = loading. [] once resolved (may be empty for non-employees or a
  // deny-by-default fallback if the effective-perms endpoint fails).
  const [permissions, setPermissions] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    // user === null → still loading /auth/me. user === false → guest.
    if (user === null) { setPermissions(null); setLoaded(false); return; }
    if (!user) { setPermissions([]); setLoaded(true); return; }
    // Buyer / supplier callers don't have employee permissions and their
    // UI is role-native. Resolve to an empty set so `can()` returns false
    // for every employee permission — role-native branches remain untouched.
    if (!EMPLOYEE_ROLES.has(user.role)) { setPermissions([]); setLoaded(true); return; }
    let cancelled = false;
    setLoaded(false);
    (async () => {
      try {
        const { data } = await api.get("/auth/me/permissions");
        if (!cancelled) {
          setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
          setLoaded(true);
        }
      } catch {
        // Fail closed — an employee who cannot fetch their own permissions
        // sees no privileged controls until they retry.
        if (!cancelled) { setPermissions([]); setLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.role, user, nonce]);

  const can = (perm) => {
    if (!loaded || !Array.isArray(permissions)) return false;
    return permissions.includes(perm);
  };

  return (
    <PermissionsContext.Provider value={{ permissions, can, loaded, refresh: () => setNonce((n) => n + 1) }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionsContext);
